import { collectBlockAndChildren } from '@lighthouse/block'
import type { BlockAbstract, ContainerBlockAbstract } from '@lighthouse/core'
import { findBlockById, findParentBlockById, getCurrentBlockChildren, useAtomAction } from '@lighthouse/shared'
import { current, original } from 'immer'
import { useAtomCallback } from 'jotai/utils'
import { useCallback } from 'react'

import { syncComponentsAtom } from '@/atoms/application/state'
import { clearSelectionAtom, createBlockAtom, initContainerRuntimeStateAtom, removeBlockAtom } from '@/atoms/page/action'
import { blockCreatingListAtom, blocksAtom, containerRuntimeStateAtomFamily, pageBlocksAtom } from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'
import * as srv from '@/services'
import { NODE_UPDATE_ACTION, pageUndoRedoController } from '@/utils/undoRedo/page/controller'

import { copyBlock, removeBlockById } from './utils'

interface Params {
    pageId: string
    stackId: string
    clearSelected?: boolean
}

type BlockUndoRedo<T extends BlockAbstract = BlockAbstract> = {
    prevBlocks?: T[]
    nextBlocks?: T[]
}

/**
 * 一些操作nodes的方法
 * @returns actions functions
 */
export const useNodeToolbarActions = ({ stackId, pageId, clearSelected }: Params) => {
    const { run: createBlock } = useAtomAction(createBlockAtom)
    const { run: removeBlock } = useAtomAction(removeBlockAtom)

    // 克隆block
    // 克隆block
    const onClone = useAtomCallback<void, [NodeIdWithScope]>(
        useCallback(
            async (get, set, { scope, id }) => {
                const getContainerState = (containerId: string) =>
                    get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined

                // 更改同步组件内
                if (scope) {
                    const syncUndoRedoInfo: {
                        createdBlock: BlockAbstract | undefined
                        prevBlocks: ContainerBlockAbstract[] | undefined
                        nextBlocks: ContainerBlockAbstract[] | undefined
                    } = {
                        createdBlock: undefined,
                        prevBlocks: undefined,
                        nextBlocks: undefined
                    }
                    set(syncComponentsAtom, draft => {
                        syncUndoRedoInfo.prevBlocks = original(draft)
                        const draftParentBlock = findParentBlockById(id, draft)
                        const children = draftParentBlock
                            ? getCurrentBlockChildren(draftParentBlock, {
                                  getContainerState,
                                  uniqueContainerId: draftParentBlock.isMasterSynchronous ? scope : `${scope}@${draftParentBlock.id}`
                              })
                            : draft
                        if (!children) {
                            return
                        }

                        const index = children.findIndex(item => item.id === id)
                        if (index === -1) {
                            return
                        }
                        const copiedBlock = current(children)[index]
                        const newBlock = copyBlock([copiedBlock])[0]
                        syncUndoRedoInfo.createdBlock = newBlock

                        set(blockCreatingListAtom, s => [
                            ...s,
                            ...collectBlockAndChildren(syncUndoRedoInfo.createdBlock!).map(item => item.id)
                        ])

                        children.splice(index + 1, 0, newBlock)

                        syncUndoRedoInfo.nextBlocks = current(draft)
                    })

                    if (syncUndoRedoInfo.prevBlocks && syncUndoRedoInfo.nextBlocks && syncUndoRedoInfo.createdBlock) {
                        set(initContainerRuntimeStateAtom, {
                            stackId,
                            blocks: get(pageBlocksAtom(pageId)),
                            syncComponents: syncUndoRedoInfo.nextBlocks
                        })

                        await srv.createSyncBlock({ createBlocks: [syncUndoRedoInfo.createdBlock], blocks: syncUndoRedoInfo.nextBlocks })
                        set(blockCreatingListAtom, s =>
                            s.filter(id => !collectBlockAndChildren(syncUndoRedoInfo.createdBlock!).some(item => item.id === id))
                        )
                        pageUndoRedoController.add({
                            action: NODE_UPDATE_ACTION,
                            payload: {
                                created: {
                                    createdSyncComponents: [syncUndoRedoInfo.createdBlock],
                                    prevSyncComponents: syncUndoRedoInfo.prevBlocks,
                                    nextSyncComponents: syncUndoRedoInfo.nextBlocks
                                }
                            }
                        })
                    }

                    return
                }

                const undoRedoInfo: {
                    createdBlock: BlockAbstract | undefined
                    prevBlocks: BlockAbstract[] | undefined
                    nextBlocks: BlockAbstract[] | undefined
                } = {
                    createdBlock: undefined,
                    prevBlocks: undefined,
                    nextBlocks: undefined
                }

                set(blocksAtom, draft => {
                    const pageBlocks = draft[pageId]
                    if (!pageBlocks) {
                        return
                    }
                    undoRedoInfo.prevBlocks = original(pageBlocks)

                    const copiedBlock = findBlockById(id, current(pageBlocks))
                    if (!copiedBlock) {
                        return
                    }

                    const parentBlock = findParentBlockById(id, pageBlocks)

                    const children = parentBlock ? getCurrentBlockChildren(parentBlock, { getContainerState }) : pageBlocks
                    if (!children) {
                        return
                    }

                    const index = children.findIndex(item => item.id === copiedBlock.id)
                    if (index === -1) {
                        return
                    }

                    // 如果复制的容器，则递归复制容器内的子元素
                    undoRedoInfo.createdBlock = copyBlock([copiedBlock])[0]

                    set(blockCreatingListAtom, s => [...s, ...collectBlockAndChildren(undoRedoInfo.createdBlock!).map(item => item.id)])

                    children.splice(index + 1, 0, undoRedoInfo.createdBlock)

                    undoRedoInfo.nextBlocks = current(pageBlocks)
                })

                if (!undoRedoInfo.createdBlock || !undoRedoInfo.prevBlocks || !undoRedoInfo.nextBlocks) {
                    return
                }

                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        created: {
                            createdBlocks: [undoRedoInfo.createdBlock],
                            prevBlocks: undoRedoInfo.prevBlocks,
                            nextBlocks: undoRedoInfo.nextBlocks
                        }
                    }
                })

                await createBlock({ pageId, stackId, blocks: undoRedoInfo.nextBlocks, createdBlocks: [undoRedoInfo.createdBlock] })
                set(blockCreatingListAtom, s =>
                    s.filter(id => !collectBlockAndChildren(undoRedoInfo.createdBlock!).some(item => item.id === id))
                )
            },
            [createBlock, pageId, stackId]
        )
    )

    // 删除block
    const onRemove = useAtomCallback<void, [NodeIdWithScope[]]>(
        useCallback(
            (get, set, nodes) => {
                const blocksUndoRedoInfo: {
                    removedBlocks: BlockAbstract[]
                    prevBlocks?: BlockAbstract[]
                    nextBlocks?: BlockAbstract[]
                } = { removedBlocks: [] }
                const syncComponentsUndoRedoInfo: {
                    removedBlocks: BlockAbstract[]
                    prevBlocks?: ContainerBlockAbstract[]
                    nextBlocks?: ContainerBlockAbstract[]
                } = { removedBlocks: [] }

                const scopeNodes = nodes.filter(item => !!item.scope)
                const pageNodes = nodes.filter(item => !item.scope)

                if (scopeNodes.length > 0) {
                    set(syncComponentsAtom, draft => {
                        scopeNodes.forEach(node => {
                            const removed = removeBlockById(node.id)(draft)
                            if (removed) {
                                syncComponentsUndoRedoInfo.removedBlocks.push(...removed.map(current))
                            }
                        })

                        syncComponentsUndoRedoInfo.prevBlocks = original(draft)
                        syncComponentsUndoRedoInfo.nextBlocks = current(draft)
                    })
                }

                if (pageNodes.length > 0) {
                    set(blocksAtom, draft => {
                        const draftBlocks = draft[pageId]
                        if (!draftBlocks) {
                            return
                        }

                        pageNodes.forEach(node => {
                            const removed = removeBlockById(node.id)(draftBlocks)
                            if (removed) {
                                blocksUndoRedoInfo.removedBlocks.push(...removed.map(current))
                            }
                        })

                        blocksUndoRedoInfo.prevBlocks = original(draftBlocks)
                        blocksUndoRedoInfo.nextBlocks = current(draftBlocks)
                    })
                }

                if (syncComponentsUndoRedoInfo.removedBlocks.length > 0 && syncComponentsUndoRedoInfo.nextBlocks) {
                    set(clearSelectionAtom, stackId)
                    srv.deleteSyncLeafBlocks({
                        pageId,
                        removedBlocks: syncComponentsUndoRedoInfo.removedBlocks,
                        blocks: syncComponentsUndoRedoInfo.nextBlocks
                    })
                }

                if (blocksUndoRedoInfo.removedBlocks.length > 0 && blocksUndoRedoInfo.nextBlocks) {
                    removeBlock({
                        pageId,
                        stackId,
                        blocks: blocksUndoRedoInfo.nextBlocks,
                        removedBlocks: blocksUndoRedoInfo.removedBlocks,
                        clearSelected
                    })
                }

                pageUndoRedoController.add({
                    action: NODE_UPDATE_ACTION,
                    payload: {
                        removed:
                            blocksUndoRedoInfo.removedBlocks.length > 0 && blocksUndoRedoInfo.prevBlocks && blocksUndoRedoInfo.nextBlocks
                                ? {
                                      removedBlocks: blocksUndoRedoInfo.removedBlocks,
                                      prevBlocks: blocksUndoRedoInfo.prevBlocks,
                                      nextBlocks: blocksUndoRedoInfo.nextBlocks
                                  }
                                : undefined
                        // prev: {
                        //     blocks: undefined,
                        //     syncComponents: syncComponentsUndoRedoInfo.prevBlocks
                        // },
                        // next: {
                        //     blocks: undefined,
                        //     syncComponents: syncComponentsUndoRedoInfo.nextBlocks
                        // }
                    }
                })
            },
            [clearSelected, pageId, removeBlock, stackId]
        )
    )

    // 交换顺序
    const onSwap = useAtomCallback<void, [NodeIdWithScope, NodeIdWithScope]>(
        useCallback(
            (get, set, from, to) => {
                if (from.scope === to.scope && from.id === to.id) {
                    return
                }

                const getContainerState = (containerId: string) =>
                    get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined

                function recursionSwap(blocks: BlockAbstract[]) {
                    let fromIndex: number | undefined
                    let toIndex: number | undefined
                    for (const [index, node] of blocks.entries()) {
                        if (node.id === from.id) {
                            fromIndex = index
                        }

                        if (node.id === to.id) {
                            toIndex = index
                        }
                        if (fromIndex !== undefined && toIndex !== undefined && fromIndex !== toIndex) {
                            ;[blocks[fromIndex], blocks[toIndex]] = [blocks[toIndex], blocks[fromIndex]]
                            return true
                        }

                        const children = getCurrentBlockChildren(node, {
                            getContainerState,
                            uniqueContainerId: node.isMasterSynchronous
                                ? from.scope
                                : node.isLeafSynchronous
                                ? `${from.scope}@${node.id}`
                                : node.id
                        })
                        if (children) {
                            const isSwapped = recursionSwap(children)
                            if (isSwapped) {
                                return true
                            }
                        }
                    }

                    return false
                }

                if (from.scope && to.scope) {
                    const syncComponentsUndoRedoInfo: BlockUndoRedo<ContainerBlockAbstract> = {}
                    set(syncComponentsAtom, draft => {
                        syncComponentsUndoRedoInfo.prevBlocks = original(draft)

                        const success = recursionSwap(draft)
                        if (success) {
                            syncComponentsUndoRedoInfo.nextBlocks = current(draft)
                        }
                    })

                    if (syncComponentsUndoRedoInfo.nextBlocks) {
                        srv.updateSyncComponents(syncComponentsUndoRedoInfo.nextBlocks)
                        pageUndoRedoController.add({
                            action: NODE_UPDATE_ACTION,
                            payload: {
                                prev: {
                                    blocks: undefined,
                                    syncComponents: syncComponentsUndoRedoInfo.prevBlocks
                                },
                                next: {
                                    blocks: undefined,
                                    syncComponents: syncComponentsUndoRedoInfo.nextBlocks
                                }
                            }
                        })
                    }
                } else {
                    const blocksUndoRedoInfo: BlockUndoRedo = {}
                    set(blocksAtom, draft => {
                        const pageBlocks = draft[pageId]
                        if (!pageBlocks) {
                            return
                        }
                        blocksUndoRedoInfo.prevBlocks = original(pageBlocks)

                        const success = recursionSwap(pageBlocks)
                        if (success) {
                            blocksUndoRedoInfo.nextBlocks = current(pageBlocks)
                        }
                    })

                    if (blocksUndoRedoInfo.nextBlocks) {
                        srv.updateBlock({
                            allBlocks: blocksUndoRedoInfo.nextBlocks,
                            pageId
                        })
                        pageUndoRedoController.add({
                            action: NODE_UPDATE_ACTION,
                            payload: {
                                prev: {
                                    blocks: blocksUndoRedoInfo.prevBlocks,
                                    syncComponents: undefined
                                },
                                next: {
                                    blocks: blocksUndoRedoInfo.nextBlocks,
                                    syncComponents: undefined
                                }
                            }
                        })
                    }
                }
            },
            [pageId, stackId]
        )
    )

    return {
        onClone,
        onRemove,
        onSwap
    }
}
